/**
 * Collection Header Component
 * Handles filter dropdowns and mobile drawer functionality
 */

class CollectionHeader {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    
    // Dropdowns
    this.filterDropdowns = section.querySelectorAll('[data-filter]');
    this.activeDropdown = null;
    
    // Mobile drawer
    this.drawer = section.querySelector('.collection-header__drawer');
    this.drawerTrigger = section.querySelector('.collection-header__mobile-filter-btn');
    this.drawerClose = section.querySelector('.collection-header__drawer-close');
    this.drawerOverlay = section.querySelector('.collection-header__drawer-overlay');
    
    this.init();
  }
  
  init() {
    this.initDropdowns();
    this.initDrawer();
    this.initSortSync();
    this.initFilters();
    this.initReadMore();
  }
  
  /**
   * Initialize read more functionality for description
   */
  initReadMore() {
    const readMoreBtn = this.section.querySelector('[data-read-more]');
    if (!readMoreBtn) return;
    
    const shortDesc = this.section.querySelector('[data-description-short]');
    const fullDesc = this.section.querySelector('[data-description-full]');
    const readMoreText = readMoreBtn.querySelector('.collection-header__read-more-text');
    
    readMoreBtn.addEventListener('click', () => {
      const isExpanded = readMoreBtn.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // Collapse
        shortDesc.style.display = '-webkit-box';
        if (fullDesc) fullDesc.style.display = 'none';
        readMoreBtn.setAttribute('aria-expanded', 'false');
        if (readMoreText) readMoreText.textContent = 'Read more';
      } else {
        // Expand
        shortDesc.style.display = 'none';
        if (fullDesc) fullDesc.style.display = 'block';
        readMoreBtn.setAttribute('aria-expanded', 'true');
        if (readMoreText) readMoreText.textContent = 'Read less';
      }
    });
  }
  
  /**
   * Initialize filter checkboxes and form handling
   */
  initFilters() {
    // Handle desktop filter checkboxes - sync with mobile and submit
    const filterCheckboxes = this.section.querySelectorAll('.collection-header__filter-checkbox input[type="checkbox"]');
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        // Sync with mobile drawer checkbox
        this.syncCheckboxState(checkbox.name, checkbox.value, checkbox.checked);
        this.submitFilters();
      });
    });
    
    // Handle mobile drawer filter checkboxes - sync with desktop
    const drawerCheckboxes = this.section.querySelectorAll('.collection-header__drawer-checkbox input[type="checkbox"]');
    drawerCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        // Sync with desktop checkbox but don't submit yet (wait for apply button)
        this.syncCheckboxState(checkbox.name, checkbox.value, checkbox.checked);
      });
    });
    
    // Handle apply button in mobile drawer
    const applyButton = this.section.querySelector('[data-apply-filters]');
    if (applyButton) {
      applyButton.addEventListener('click', () => {
        this.closeDrawer();
        this.submitFilters();
      });
    }
    
    // Initialize price range sliders
    this.initPriceSliders();
  }
  
  /**
   * Sync checkbox state between desktop and mobile
   */
  syncCheckboxState(name, value, checked) {
    const allCheckboxes = this.section.querySelectorAll(`input[type="checkbox"][name="${name}"][value="${value}"]`);
    allCheckboxes.forEach(cb => {
      cb.checked = checked;
    });
  }
  
  /**
   * Sync price values between desktop and mobile price filters
   */
  syncPriceValues(minValue, maxValue) {
    const priceFilters = this.section.querySelectorAll('[data-price-filter]');
    priceFilters.forEach(filter => {
      const minInput = filter.querySelector('input[name*=".gte"]');
      const maxInput = filter.querySelector('input[name*=".lte"]');
      const minDisplay = filter.querySelector('.collection-header__filter-price-min, .collection-header__drawer-price-min');
      const maxDisplay = filter.querySelector('.collection-header__filter-price-max, .collection-header__drawer-price-max');
      
      // Store dollar values (rounded)
      if (minInput) minInput.value = Math.round(minValue);
      if (maxInput) maxInput.value = Math.round(maxValue);
      if (minDisplay) minDisplay.textContent = this.formatMoney(minValue);
      if (maxDisplay) maxDisplay.textContent = this.formatMoney(maxValue);
    });
  }
  
  /**
   * Initialize price range sliders
   */
  initPriceSliders() {
    const priceFilters = this.section.querySelectorAll('[data-price-filter]');
    
    priceFilters.forEach(filter => {
      const slider = filter.querySelector('.collection-header__filter-price-slider, .collection-header__drawer-price-slider');
      const thumbMin = filter.querySelector('.collection-header__filter-price-thumb-min, .collection-header__drawer-price-thumb-min');
      const thumbMax = filter.querySelector('.collection-header__filter-price-thumb-max, .collection-header__drawer-price-thumb-max');
      const fill = filter.querySelector('.collection-header__filter-price-fill, .collection-header__drawer-price-fill');
      // Find inputs by position - first is min, second is max
      const inputs = filter.querySelectorAll('input[type="hidden"]');
      const minInput = inputs[0];
      const maxInput = inputs[1];
      const minDisplay = filter.querySelector('.collection-header__filter-price-min, .collection-header__drawer-price-min');
      const maxDisplay = filter.querySelector('.collection-header__filter-price-max, .collection-header__drawer-price-max');
      
      if (!slider || !thumbMin || !thumbMax) return;
      
      const rangeMax = Math.round(parseFloat(filter.dataset.rangeMax) || 1000);
      let minValue = Math.round(parseFloat(filter.dataset.min) || 0);
      let maxValue = Math.round(parseFloat(filter.dataset.max) || rangeMax);
      
      const updateUI = () => {
        const minPercent = (minValue / rangeMax) * 100;
        const maxPercent = (maxValue / rangeMax) * 100;
        
        thumbMin.style.left = `${minPercent}%`;
        thumbMax.style.left = `${maxPercent}%`;
        
        if (fill) {
          fill.style.left = `${minPercent}%`;
          fill.style.right = `${100 - maxPercent}%`;
        }

        thumbMin.dataset.value = minValue;
        thumbMax.dataset.value = maxValue;
        thumbMin.setAttribute('aria-valuenow', String(minValue));
        thumbMax.setAttribute('aria-valuenow', String(maxValue));
        thumbMin.setAttribute('aria-valuemax', String(maxValue));
        thumbMax.setAttribute('aria-valuemin', String(minValue));
        thumbMin.setAttribute('aria-valuetext', this.formatMoney(minValue));
        thumbMax.setAttribute('aria-valuetext', this.formatMoney(maxValue));
        
        if (minDisplay) minDisplay.textContent = this.formatMoney(minValue);
        if (maxDisplay) maxDisplay.textContent = this.formatMoney(maxValue);
        
        // Store dollar values in hidden inputs (Shopify accepts dollar values)
        if (minInput) minInput.value = Math.round(minValue);
        if (maxInput) maxInput.value = Math.round(maxValue);
      };
      
      // Initialize
      updateUI();
      
      // Drag handling
      let isDragging = false;
      let currentThumb = null;
      
      const handleDragStart = (e, thumb) => {
        isDragging = true;
        currentThumb = thumb;
        e.preventDefault();
      };
      
      const handleDragMove = (e) => {
        if (!isDragging || !currentThumb) return;
        
        const rect = slider.getBoundingClientRect();
        const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        let percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.max(0, Math.min(100, percent));
        
        const value = Math.round((percent / 100) * rangeMax);
        
        if (currentThumb === thumbMin) {
          minValue = Math.min(value, maxValue);
        } else {
          maxValue = Math.max(value, minValue);
        }
        
        updateUI();
      };
      
      const handleDragEnd = () => {
        if (isDragging) {
          isDragging = false;
          currentThumb = null;

          this.syncAndSubmitPriceFilter(filter, minValue, maxValue);
        }
      };

      const step = 1;
      const largeStep = Math.max(10, Math.round(rangeMax / 10));
      const handleThumbKeydown = (event, thumb) => {
        let nextMin = minValue;
        let nextMax = maxValue;

        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowDown':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = Math.max(0, minValue - step);
            } else {
              nextMax = Math.max(minValue, maxValue - step);
            }
            break;
          case 'ArrowRight':
          case 'ArrowUp':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = Math.min(maxValue, minValue + step);
            } else {
              nextMax = Math.min(rangeMax, maxValue + step);
            }
            break;
          case 'PageDown':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = Math.max(0, minValue - largeStep);
            } else {
              nextMax = Math.max(minValue, maxValue - largeStep);
            }
            break;
          case 'PageUp':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = Math.min(maxValue, minValue + largeStep);
            } else {
              nextMax = Math.min(rangeMax, maxValue + largeStep);
            }
            break;
          case 'Home':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = 0;
            } else {
              nextMax = minValue;
            }
            break;
          case 'End':
            event.preventDefault();
            if (thumb === thumbMin) {
              nextMin = maxValue;
            } else {
              nextMax = rangeMax;
            }
            break;
          default:
            return;
        }

        minValue = nextMin;
        maxValue = nextMax;
        updateUI();
        this.syncAndSubmitPriceFilter(filter, minValue, maxValue);
      };
      
      // Mouse events
      thumbMin.addEventListener('mousedown', (e) => handleDragStart(e, thumbMin));
      thumbMax.addEventListener('mousedown', (e) => handleDragStart(e, thumbMax));
      thumbMin.addEventListener('keydown', (event) => handleThumbKeydown(event, thumbMin));
      thumbMax.addEventListener('keydown', (event) => handleThumbKeydown(event, thumbMax));
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      
      // Touch events
      thumbMin.addEventListener('touchstart', (e) => handleDragStart(e, thumbMin), { passive: false });
      thumbMax.addEventListener('touchstart', (e) => handleDragStart(e, thumbMax), { passive: false });
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
      
      // Click on track to move nearest thumb
      slider.addEventListener('click', (e) => {
        if (e.target === thumbMin || e.target === thumbMax) return;
        
        const rect = slider.getBoundingClientRect();
        const percent = ((e.clientX - rect.left) / rect.width) * 100;
        const value = Math.round((percent / 100) * rangeMax);
        
        const distToMin = Math.abs(value - minValue);
        const distToMax = Math.abs(value - maxValue);
        
        if (distToMin < distToMax) {
          minValue = Math.min(value, maxValue);
        } else {
          maxValue = Math.max(value, minValue);
        }
        
        updateUI();

        this.syncAndSubmitPriceFilter(filter, minValue, maxValue);
      });
    });
  }
  
  /**
   * Format money value
   */
  formatMoney(value) {
    return Money.format(parseFloat(value) * 100);
  }
  
  /**
   * Submit filter form
   */
  submitFilters() {
    const params = new URLSearchParams();
    const addedParams = new Set();
    
    // Collect all checked checkboxes - group by name to handle duplicates
    const checkboxGroups = new Map();
    
    // Get all checkboxes (both checked and unchecked) to properly sync states
    const allCheckboxes = this.section.querySelectorAll('.collection-header__filter-checkbox input[type="checkbox"], .collection-header__drawer-checkbox input[type="checkbox"]');
    
    allCheckboxes.forEach(checkbox => {
      const key = checkbox.name + checkbox.value;
      if (!checkboxGroups.has(key)) {
        checkboxGroups.set(key, {
          name: checkbox.name,
          value: checkbox.value,
          checked: checkbox.checked
        });
      } else {
        // If any instance is checked, mark as checked (OR logic)
        if (checkbox.checked) {
          checkboxGroups.get(key).checked = true;
        }
      }
    });
    
    // Sync all checkbox instances to match the final state
    allCheckboxes.forEach(checkbox => {
      const key = checkbox.name + checkbox.value;
      const groupState = checkboxGroups.get(key);
      if (groupState) {
        checkbox.checked = groupState.checked;
      }
    });
    
    // Add only checked filters to params
    checkboxGroups.forEach((group, key) => {
      if (group.checked && group.name && group.value && !addedParams.has(key)) {
        params.append(group.name, group.value);
        addedParams.add(key);
      }
    });
    
    // Collect price range values from hidden inputs
    const priceFilters = this.section.querySelectorAll('[data-price-filter]');
    priceFilters.forEach(filter => {
      // Find inputs by their param name - look for filter.v.price.gte and filter.v.price.lte
      const allHiddenInputs = filter.querySelectorAll('input[type="hidden"]');
      let minInput = null;
      let maxInput = null;
      
      allHiddenInputs.forEach(input => {
        if (input.name && input.name.includes('.gte')) {
          minInput = input;
        } else if (input.name && input.name.includes('.lte')) {
          maxInput = input;
        }
      });
      
      // Use Math.round to avoid floating point precision issues
      const rangeMax = Math.round(parseFloat(filter.dataset.rangeMax) || 1000);
      
      // Only add min if it's greater than 0
      if (minInput && minInput.name && !addedParams.has(minInput.name)) {
        const minVal = Math.round(parseFloat(minInput.value) || 0);
        if (minVal > 0) {
          params.append(minInput.name, minVal);
          addedParams.add(minInput.name);
        }
      }
      
      // Only add max if it's less than the maximum range (not equal)
      if (maxInput && maxInput.name && !addedParams.has(maxInput.name)) {
        const maxVal = Math.round(parseFloat(maxInput.value) || 0);
        if (maxVal > 0 && maxVal < rangeMax) {
          params.append(maxInput.name, maxVal);
          addedParams.add(maxInput.name);
        }
      }
    });
    
    // Preserve sort param
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('sort_by')) {
      params.set('sort_by', urlParams.get('sort_by'));
    }
    
    // Navigate to filtered URL
    const queryString = params.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.location.href = newUrl;
  }
  
  /**
   * Initialize filter dropdowns
   */
  initDropdowns() {
    this.filterDropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.collection-header__filter-trigger');
      const menu = dropdown.querySelector('.collection-header__filter-menu');
      
      if (!trigger || !menu) return;
      
      // Click to toggle
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown(dropdown);
      });
      
      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        this.handleTriggerKeydown(e, dropdown);
      });
      
      // Menu keyboard navigation
      menu.addEventListener('keydown', (e) => {
        this.handleMenuKeydown(e, dropdown);
      });
      
      // Handle clicks inside dropdown menu (prevent closing when interacting with filters)
      menu.addEventListener('click', (e) => {
        // Don't close if clicking on checkbox, label, price slider, or any interactive element
        if (e.target.closest('.collection-header__filter-checkbox') || 
            e.target.closest('[data-price-filter]') ||
            e.target.closest('input[type="checkbox"]') ||
            e.target.closest('label') ||
            e.target.closest('button')) {
          e.stopPropagation();
        }
      });
    });
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (this.activeDropdown && !this.activeDropdown.contains(e.target)) {
        this.closeDropdown(this.activeDropdown);
      }
    });
    
    // Close dropdowns on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeDropdown) {
        const activeDropdown = this.activeDropdown;
        const trigger = activeDropdown.querySelector('.collection-header__filter-trigger');
        this.closeDropdown(activeDropdown);
        trigger?.focus();
      }
    });
  }
  
  /**
   * Toggle dropdown open/closed
   */
  toggleDropdown(dropdown) {
    if (dropdown.classList.contains('is-open')) {
      this.closeDropdown(dropdown);
    } else {
      // Close any other open dropdown first
      if (this.activeDropdown && this.activeDropdown !== dropdown) {
        this.closeDropdown(this.activeDropdown);
      }
      this.openDropdown(dropdown);
    }
  }
  
  /**
   * Open dropdown
   */
  openDropdown(dropdown) {
    const trigger = dropdown.querySelector('.collection-header__filter-trigger');
    const menu = dropdown.querySelector('.collection-header__filter-menu');
    
    dropdown.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    menu.removeAttribute('hidden');
    
    // Focus first interactive control
    const firstFocusable = this.getFocusableDropdownItems(menu)[0];
    firstFocusable?.focus();
    
    this.activeDropdown = dropdown;
  }
  
  /**
   * Close dropdown
   */
  closeDropdown(dropdown) {
    const trigger = dropdown.querySelector('.collection-header__filter-trigger');
    const menu = dropdown.querySelector('.collection-header__filter-menu');
    
    dropdown.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    menu.setAttribute('hidden', '');
    
    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    }
  }
  
  /**
   * Handle trigger keyboard navigation
   */
  handleTriggerKeydown(e, dropdown) {
    const menu = dropdown.querySelector('.collection-header__filter-menu');
    const focusableItems = this.getFocusableDropdownItems(menu);
    
    switch (e.key) {
      case 'ArrowDown':
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.openDropdown(dropdown);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.openDropdown(dropdown);
        focusableItems[focusableItems.length - 1]?.focus();
        break;
    }
  }
  
  /**
   * Handle menu keyboard navigation
   */
  handleMenuKeydown(e, dropdown) {
    const menu = dropdown.querySelector('.collection-header__filter-menu');
    const trigger = dropdown.querySelector('.collection-header__filter-trigger');
    const sortOptions = Array.from(menu.querySelectorAll('.collection-header__filter-option'));
    const currentIndex = sortOptions.indexOf(document.activeElement);
    
    switch (e.key) {
      case 'ArrowDown':
        if (!sortOptions.length || currentIndex === -1) return;
        e.preventDefault();
        sortOptions[(currentIndex + 1) % sortOptions.length]?.focus();
        break;
        
      case 'ArrowUp':
        if (!sortOptions.length || currentIndex === -1) return;
        e.preventDefault();
        sortOptions[currentIndex <= 0 ? sortOptions.length - 1 : currentIndex - 1]?.focus();
        break;
        
      case 'Home':
        if (!sortOptions.length) return;
        e.preventDefault();
        sortOptions[0]?.focus();
        break;
        
      case 'End':
        if (!sortOptions.length) return;
        e.preventDefault();
        sortOptions[sortOptions.length - 1]?.focus();
        break;
        
      case 'Tab':
        window.requestAnimationFrame(() => {
          if (!dropdown.contains(document.activeElement)) {
            this.closeDropdown(dropdown);
          }
        });
        break;
        
      case 'Escape':
        e.preventDefault();
        this.closeDropdown(dropdown);
        trigger?.focus();
        break;
    }
  }
  
  /**
   * Select an option (for sort dropdown)
   */
  selectOption(dropdown, option) {
    const isSort = dropdown.dataset.filter === 'sort';
    
    if (isSort) {
      // Update the current sort label
      const currentLabel = dropdown.querySelector('.collection-header__sort-current');
      if (currentLabel) {
        currentLabel.textContent = option.textContent;
      }
      
      // Update active state
      const options = dropdown.querySelectorAll('.collection-header__filter-option');
      options.forEach(opt => opt.classList.remove('is-active'));
      option.classList.add('is-active');
    }
    
    // Close dropdown
    this.closeDropdown(dropdown);
  }

  getFocusableDropdownItems(menu) {
    return Array.from(
      menu.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null || element === document.activeElement);
  }

  syncAndSubmitPriceFilter(filter, minValue, maxValue) {
    this.syncPriceValues(minValue, maxValue);

    const isInDrawer = filter.closest('.collection-header__drawer') !== null;
    if (!isInDrawer) {
      this.submitFilters();
    }
  }
  
  /**
   * Initialize mobile drawer
   */
  initDrawer() {
    if (!this.drawer || !this.drawerTrigger) return;
    
    // Open drawer
    this.drawerTrigger.addEventListener('click', () => {
      this.openDrawer();
    });
    
    // Close drawer
    this.drawerClose?.addEventListener('click', () => {
      this.closeDrawer();
    });
    
    this.drawerOverlay?.addEventListener('click', () => {
      this.closeDrawer();
    });
    
    // Keyboard close
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
        this.drawerTrigger?.focus();
      }
    });
    
    // Trap focus in drawer
    this.drawer.addEventListener('keydown', (e) => {
      this.trapFocus(e);
    });
  }
  
  /**
   * Open mobile drawer
   */
  openDrawer() {
    // Move drawer to body to ensure it's above all other elements
    if (this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }
    
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawerTrigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('collection-header__drawer-open');
    
    // Focus close button
    setTimeout(() => {
      this.drawerClose?.focus();
    }, 100);
  }
  
  /**
   * Close mobile drawer
   */
  closeDrawer() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawerTrigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    document.body.classList.remove('collection-header__drawer-open');
    
    // Return drawer to its section after transition
    setTimeout(() => {
      if (this.section && this.drawer.parentElement !== this.section) {
        this.section.appendChild(this.drawer);
      }
    }, 300);
  }
  
  /**
   * Trap focus within drawer
   */
  trapFocus(e) {
    if (e.key !== 'Tab') return;
    
    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }
  
  /**
   * Sync sort dropdown with URL parameters
   */
  initSortSync() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by') || 'manual';
    
    const sortDropdown = this.section.querySelector('[data-filter="sort"]');
    if (!sortDropdown) return;
    
    const sortOptions = sortDropdown.querySelectorAll('.collection-header__filter-option');
    const currentLabel = sortDropdown.querySelector('.collection-header__sort-current');
    
    sortOptions.forEach(option => {
      const value = option.dataset.value;
      
      // Set active state based on URL
      if (value === sortBy) {
        option.classList.add('is-active');
        if (currentLabel) {
          currentLabel.textContent = option.textContent;
        }
      } else {
        option.classList.remove('is-active');
      }
      
      // Update href to preserve other params
      const currentUrl = new URL(option.href);
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set('sort_by', value);
      currentUrl.search = currentParams.toString();
      option.href = currentUrl.toString();
    });
  }
}

if (!customElements.get('collection-header-section')) {
  class CollectionHeaderSection extends HTMLElement {
    connectedCallback() {
      if (!this.collectionHeader) {
        this.collectionHeader = new CollectionHeader(this);
      }
    }
  }
  customElements.define('collection-header-section', CollectionHeaderSection);
}

